import React, { useState } from "react";
import {
  AppBar,
  Toolbar,
  IconButton,
  Typography,
  Drawer,
  Box,
  MenuItem,
  Badge,
  Checkbox,
  FormControlLabel,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import LogoutIcon from "@mui/icons-material/LogoutOutlined";
import AccountIcon from "@mui/icons-material/AccountCircle";
import HomeIcon from "@mui/icons-material/Home";
import { Link } from "react-router-dom";
import { useSearch } from "../../contexts/SearchContext";
import SearchBar from "../SearchBar";
import LoginCircle from "@mui/icons-material/LogoutOutlined";

interface UserNavbarProps {
  isAuthenticated: boolean;
  cartItems: number;
  handleLogout: () => void;
  showSearch?: boolean;
}

const UserNavbar: React.FC<UserNavbarProps> = ({
  isAuthenticated,
  cartItems,
  handleLogout,
  showSearch,
}) => {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [isTransparent, setIsTransparent] = useState(false);
  const { searchTerm, setSearchTerm } = useSearch();

  const toggleDrawer = (open: boolean) => setDrawerOpen(open);

  const handleDrawerClick = (event: React.MouseEvent) => {
    event.stopPropagation();
  };

  const handleDrawerKeyDown = (event: React.KeyboardEvent) => {
    if (event.target instanceof HTMLInputElement) {
      event.stopPropagation();
    }
  };

  const handleTransparencyChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setIsTransparent(event.target.checked);
  };

  return (
    <>
      <AppBar position="sticky" sx={{ backgroundColor: "#1976d2", overflow: "hidden" }}>
        <Toolbar
          sx={{
            display: "flex",
            justifyContent: { xs: "space-between", sm: "flex-start" },
            gap: 2,
            maxWidth: "100%",
          }}
        >
          <IconButton
            color="inherit"
            edge="start"
            onClick={() => toggleDrawer(true)}
            sx={{ display: { xs: "block", sm: "none" } }}
          >
            <MenuIcon />
          </IconButton>

          <Typography
            variant="h6"
            component={Link}
            to="/products"
            sx={{
              textDecoration: "none",
              color: "#fff",
              marginRight: { xs: "auto", sm: 0 },
              whiteSpace: "nowrap",
            }}
          >
            User Nav
          </Typography>

          {showSearch && (
            <Box sx={{ flexGrow: 1, display: { xs: "none", sm: "block" }, mx: "auto" }}>
              <SearchBar />
            </Box>
          )}

          <Box
            sx={{
              display: { xs: "none", sm: "flex" },
              alignItems: "center",
              gap: 2,
              marginLeft: "auto",
            }}
          >
            <IconButton color="inherit" component={Link} to="/products">
              <HomeIcon />
            </IconButton>
            <IconButton color="inherit" component={Link} to="/profile">
              <AccountIcon />
            </IconButton>
            <IconButton color="inherit" component={Link} to="/cart">
              <Badge badgeContent={cartItems} color="error">
                <ShoppingCartIcon />
              </Badge>
            </IconButton>
            <Box
              onClick={handleLogout}
              sx={{
                cursor: "pointer",
                color: "inherit",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                borderRadius: "50%",
              }}
            >
              <LoginCircle />
            </Box>
          </Box>
        </Toolbar>
      </AppBar>

      <Drawer
        anchor="left"
        open={drawerOpen}
        onClose={() => toggleDrawer(false)}
        sx={{
          display: { xs: "block", sm: "none" },
          backgroundColor: isTransparent ? "rgba(255, 255, 255, 0.12)" : "#fff",
          "& .MuiDrawer-paper": {
            backgroundColor: isTransparent ? "rgba(255, 255, 255, 0.18)" : "#fff",
          },
        }}
      >
        <Box
          sx={{ width: 250, paddingTop: 2 }}
          role="presentation"
          onClick={() => toggleDrawer(false)}
          onKeyDown={handleDrawerKeyDown}
        >
          <Box onClick={handleDrawerClick}>
          <Box sx={{ flexGrow: 1, display: "flex", paddingLeft: 1, paddingRight: 1}}>
              <SearchBar />
            </Box>
          </Box>
          <MenuItem component={Link} to="/products" onClick={() => toggleDrawer(false)}>
            Inicio
          </MenuItem>
          <MenuItem component={Link} to="/cart" onClick={() => toggleDrawer(false)}>
            Carrito ({cartItems})
          </MenuItem>
          {isAuthenticated ? (
            <>
              <MenuItem
                component={Link}
                to="/profile"
                onClick={() => toggleDrawer(false)}
              >
                Perfil
              </MenuItem>
              <MenuItem
                onClick={() => {
                  handleLogout();
                  toggleDrawer(false);
                }}
              >
                Cerrar sesión
              </MenuItem>
            </>
          ) : (
            <MenuItem
              component={Link}
              to="/login"
              onClick={() => toggleDrawer(false)}
            >
              Iniciar sesión
            </MenuItem>
          )}
          <Box sx={{ padding: 2 }} onClick={handleDrawerClick}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={isTransparent}
                  onChange={handleTransparencyChange}
                  color="primary"
                />
              }
              label="Hacer el menú transparente"
            />
          </Box>
        </Box>
      </Drawer>
    </>
  );
};

export default UserNavbar;
